import React, { useCallback, useEffect, useMemo, useState, type FC } from "react";

import { IconClock } from "@/assets/icons/geist/IconClock";
import { computeTimeoutOptionsFromSeconds, toTimeout } from "@/components/environments/environment-timeout";
import {
    ContextMenuRadioGroup,
    ContextMenuRadioItem,
    ContextMenuSub,
    ContextMenuSubContent,
    ContextMenuSubTrigger,
} from "@/components/flexkit/ContextMenu";
import { useToast } from "@/components/flexkit/toast";
import {
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { useUpdateEnvironment } from "@/queries/environment-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";

/** Self-contained auto-stop submenu. Isolated so the policies query and
 *  mutation state don't cause re-renders in the parent menu. Memoized
 *  to skip re-renders when the environment object reference changes but
 *  the timeout value hasn't. */
export const AutoStopSubmenu: FC<{
    environmentId: string;
    timeoutSeconds: number;
    /** Which menu primitive set to render with. Defaults to dropdownMenu. */
    renderAs?: "dropdownMenu" | "contextMenu";
    /** Suffix for data-tracking-id attributes, e.g. "super-ona-header". */
    trackingIdSuffix?: string;
}> = React.memo(function AutoStopSubmenu({
    environmentId,
    timeoutSeconds,
    renderAs = "dropdownMenu",
    trackingIdSuffix = "auto-stop-submenu",
}) {
    const { toast } = useToast();
    const { data: policies } = useGetOrganizationPolicies();
    const updateEnvironment = useUpdateEnvironment();

    const maxPolicySeconds = Number(policies?.maximumEnvironmentTimeout?.seconds || 0);
    const timeoutOptions = useMemo(
        () => computeTimeoutOptionsFromSeconds(timeoutSeconds, maxPolicySeconds),
        [timeoutSeconds, maxPolicySeconds],
    );
    const [timeoutValue, setTimeoutValue] = useState(() => toTimeout(timeoutSeconds));

    useEffect(() => {
        setTimeoutValue(toTimeout(timeoutSeconds));
    }, [timeoutSeconds]);

    const handleTimeoutChange = useCallback(
        async (newValue: string) => {
            const original = timeoutValue;
            try {
                const seconds = BigInt(newValue);
                setTimeoutValue(toTimeout(Number(newValue)));
                await updateEnvironment.mutateAsync({
                    req: {
                        environmentId,
                        spec: { timeout: { disconnected: { seconds } } },
                    },
                });
                toast({ title: "Successfully updated auto-stop" });
            } catch (error) {
                setTimeoutValue(original);
                toast({
                    title: "Failed to update auto-stop value",
                    description: formatError(error),
                });
            }
        },
        [timeoutValue, updateEnvironment, environmentId, toast],
    );

    const Sub = renderAs === "dropdownMenu" ? DropdownMenuSub : ContextMenuSub;
    const SubTrigger = renderAs === "dropdownMenu" ? DropdownMenuSubTrigger : ContextMenuSubTrigger;
    const SubContent = renderAs === "dropdownMenu" ? DropdownMenuSubContent : ContextMenuSubContent;
    const RadioGroup = renderAs === "dropdownMenu" ? DropdownMenuRadioGroup : ContextMenuRadioGroup;
    const RadioItem = renderAs === "dropdownMenu" ? DropdownMenuRadioItem : ContextMenuRadioItem;

    return (
        <Sub>
            <SubTrigger data-testid="menu-item-auto-stop" data-tracking-id={`auto-stop-${trackingIdSuffix}`}>
                <span className="mr-2">
                    <IconClock size="sm" />
                </span>
                <span className="truncate">Auto-stop: {timeoutValue.shortLabel}</span>
            </SubTrigger>
            <SubContent>
                <RadioGroup value={timeoutValue.value} onValueChange={handleTimeoutChange}>
                    {timeoutOptions.map((option) => (
                        <RadioItem
                            key={option.value}
                            value={option.value}
                            data-tracking-id={`auto-stop-option-${option.value}-${trackingIdSuffix}`}
                        >
                            {option.label}
                        </RadioItem>
                    ))}
                </RadioGroup>
            </SubContent>
        </Sub>
    );
});
AutoStopSubmenu.displayName = "AutoStopSubmenu";
